import {
  format as dfFormat,
  parseISO,
  isValid as dfIsValid,
  isDate as dfIsDate,
  getMonth as dfGetMonth,
  getYear as dfGetYear,
  startOfISOWeek,
  endOfISOWeek,
  endOfYear,
  add,
  addWeeks as dfAddWeeks,
  addMonths as dfAddMonths,
  addQuarters as dfAddQuarters,
  addYears as dfAddYears,
  addDays as dfAddDays,
  addHours as dfAddHours,
  addMinutes as dfAddMinutes,
  addSeconds as dfAddSeconds,
  differenceInYears,
  differenceInMonths,
  differenceInQuarters,
  differenceInWeeks,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  compareAsc,
  subHours,
  intervalToDuration,
} from 'date-fns';
import { isEmpty, isNil, path, propOr } from '../../utils/fp';
import * as rules from '../rules';
import { defaultFrequency } from '../settings';
import { getFrequencyArtefact } from './structure';

// ------------------------------------------------------------------
export const sdmxFormat = {
  A: 'yyyy', // Year
  S: 'yyyy-',
  Q: "yyyy-'Q'Q",
  M: 'yyyy-MM',
  // ISO week padded (II = 2-digit ISO week); include literal 'W'
  W: "RRRR-'W'II",
  B: "RRRR-'W'II",
  D: 'yyyy-MM-dd',
  H: "yyyy-MM-dd'T'HH:mm:ss",
  N: "yyyy-MM-dd'T'HH:mm:ss",
};

const getAnnualOffset = freq => {
  const annualMatch = freq.match(/A([\d]+)$/);
  return annualMatch ? annualMatch[1] : 1;
};

const orderedFreqs = ['N', 'H', 'D', 'B', 'W', 'M', 'Q', 'S', 'A'];

// ------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------
const toDateSafe = d => {
  if (dfIsDate(d)) return d;
  if (typeof d === 'number') return new Date(d);
  if (typeof d === 'string') {
    const parsed = parseISO(d);
    return dfIsValid(parsed) ? parsed : new Date(d);
  }
  return d;
};

const getIsValidDate = dateStrOrDate => {
  if (isNil(dateStrOrDate)) return false;
  const d = toDateSafe(dateStrOrDate);
  return dfIsDate(d) && dfIsValid(d);
};

export const getIrregularRangeScope = rangeDef => {
  const match = rangeDef.match(/\d+([YMDHS])$/);
  return match === null ? null : match[1];
};

export const getMinimalFreqScope = (freq1, freq2) => {
  const result = orderedFreqs.find(f => f === freq1 || f === freq2);
  return result ? result : defaultFrequency;
};

// ------------------------------------------------------------------
const getSemesterValue = value => (6 >= Number(value) + 1 ? 1 : 2);

// ------------------------------------------------------------------
const isWeekInPreviousYear = date => {
  const startIsoWeek = startOfISOWeek(toDateSafe(date));
  // check the years of days Monday..Thursday of that ISO week (4 days)
  const years = Array.from({ length: 4 }, (_, n) => dfGetYear(dfAddDays(startIsoWeek, n)));
  return new Set(years).size === 1 && years[0] < dfGetYear(date);
};

// ------------------------------------------------------------------
export const getEndOfYear = date => {
  const d = toDateSafe(date);
  if (!getIsValidDate(d)) return undefined;
  return endOfYear(d);
};

// ------------------------------------------------------------------
export const getAjustedDate = frequency => date => {
  const d = toDateSafe(date);
  if (isNil(d) || !getIsValidDate(d)) return d;

  if (frequency === 'W' || frequency === 'B') {
    return isWeekInPreviousYear(d) ? startOfISOWeek(d) : endOfISOWeek(d);
  }
  return d;
};
// ------------------------------------------------------------------
export const applyFormat =
  (frequency, formats = sdmxFormat, options = {}) =>
  date => {
    if (isNil(date) || !getIsValidDate(date)) return date;
    const hasFormat = Object.keys(formats).includes(frequency);
    const format = hasFormat ? formats[frequency] : formats[defaultFrequency];

    const d = getAjustedDate(frequency)(date);
    let formatted = dfFormat(d, format, options);
    if (frequency === 'S') {
      const semester = `S${getSemesterValue(dfGetMonth(d))}`;
      formatted = formatted + semester;
    }
    return formatted;
  };

const dateWithoutTZ = date => dfAddMinutes(date, date.getTimezoneOffset());

export const applyTZedFormat = freq => date => {
  const tzed = dateWithoutTZ(new Date(date));
  return applyFormat(freq)(tzed);
};

// ------------------------------------------------------------------
const parseYear = y => new Date(Number(y), 0, 1);

const addMonths = (date, n) => add(date, { months: n });

const slicerSQW = period => period.at(-1).slice(1);

const getSQW = (fn, slicer) => periods =>
  periods.map(period => {
    if (isEmpty(period[0])) return undefined;
    const year = period[0];
    const hasAdded = period.length === 2 ? slicer(period) : 1;
    return fn(Number(hasAdded))(parseYear(year));
  });

// ------------------------------------------------------------------
export function parseDateFromSdmxPeriod(frequency, sdmxPeriod) {
  if (arguments.length < 2) return sp => parseDateFromSdmxPeriod(frequency, sp);
  if (isNil(sdmxPeriod)) return sdmxPeriod;

  const periodSQW = sdmxPeriod.map((stringPeriod = '') => stringPeriod.split('-'));

  let res;

  if (!['Q', 'S', 'W', 'B'].includes(frequency)) {
    res = sdmxPeriod.map(p => {
      if (isEmpty(p)) return undefined;
      return toDateSafe(p);
    });
  } else if (['W', 'B'].includes(frequency)) {
    res = getSQW(
      n => d => dfAddWeeks(d, n - 1),
      slicerSQW,
    )(periodSQW).map(getAjustedDate(frequency));
  } else if (frequency === 'Q') {
    res = getSQW(n => d => dfAddQuarters(d, n - 1), slicerSQW)(periodSQW);
  } else {
    // semester
    res = getSQW(n => d => addMonths(d, (n - 1) * 6), slicerSQW)(periodSQW);
  }

  // NB: the original used a comma expression here, so the adjustEndDate mapping
  // below was never actually applied — res is returned as-is (behavior preserved).
  return res;
}
// ------------------------------------------------------------------
// Difference and adders
// ------------------------------------------------------------------
const getDifferenceBetweenDates = (frequency, interval) => {
  const differs = {
    A: differenceInYears,
    S: (a, b) => Math.floor(differenceInMonths(a, b) / 6),
    Q: differenceInQuarters,
    M: differenceInMonths,
    W: differenceInWeeks,
    B: differenceInWeeks,
    D: differenceInDays,
    H: differenceInHours,
    N: differenceInMinutes,
  };

  const getDiffers = freq => {
    if (freq && freq.startsWith('A')) {
      const offset = getAnnualOffset(freq);
      return (a, b) => Math.floor(differenceInYears(a, b) / offset);
    }
    return differs[freq] || differs[defaultFrequency];
  };

  const fn = getDiffers(frequency);
  return fn(interval.at(-1), interval[0]);
};

const addTimeList = {
  A: (date, v) => dfAddYears(date, v),
  S: (date, v) => dfAddMonths(date, v * 6),
  Q: (date, v) => dfAddQuarters(date, v),
  M: (date, v) => dfAddMonths(date, v),
  W: (date, v) => dfAddWeeks(date, v),
  B: (date, v) => dfAddWeeks(date, v),
  D: (date, v) => dfAddDays(date, v),
  H: (date, v) => dfAddHours(date, v),
  N: (date, v) => dfAddMinutes(date, v),
};

const getAddTimeList = freq => {
  if (freq && freq.startsWith('A')) {
    const offset = getAnnualOffset(freq);
    return (date, v) => dfAddYears(date, v * offset);
  }
  return addTimeList[freq] || addTimeList[defaultFrequency];
};

// ------------------------------------------------------------------
// Helper wrappers to keep original behaviour
// ------------------------------------------------------------------
export const getFrequencyFromSdmxPeriod = sdmxPeriod => {
  if (isNil(sdmxPeriod)) {
    return defaultFrequency;
  }
  if (sdmxPeriod.match(/^\d\d\d\d$/) !== null) {
    return 'A';
  }
  if (sdmxPeriod.match(/^\d\d\d\d-S\d$/) !== null) {
    return 'S';
  }
  if (sdmxPeriod.match(/^\d\d\d\d-Q\d$/) !== null) {
    return 'Q';
  }
  if (sdmxPeriod.match(/^\d\d\d\d-W\d\d$/) !== null) {
    return 'W';
  }
  if (sdmxPeriod.match(/^\d\d\d\d-B\d\d$/) !== null) {
    return 'B';
  }
  if (sdmxPeriod.match(/^\d\d\d\d-\d\d$/) !== null) {
    return 'M';
  }
  if (sdmxPeriod.match(/^\d\d\d\d-\d\d-\d\d$/) !== null) {
    return 'D';
  }
  if (sdmxPeriod.match(/^\d\d\d\d-\d\d-\d\dT\d\d:\d\d:\d\d$/) !== null) {
    return 'H';
  }
  return defaultFrequency;
};

export const getFrequencyFromSdmxPeriodSelection = selection => {
  if (isNil(selection)) {
    return null;
  }
  return getFrequencyFromSdmxPeriod(selection[0] || selection[1]);
};

export const getFrequencies = artefact =>
  Object.fromEntries(propOr([], 'values', artefact).map(value => [value.id, value.name]));

export const parseInclusiveDates =
  (inclusives, frequency = defaultFrequency) =>
  dates => {
    const addTime = getAddTimeList(frequency);
    return dates.map((date, index) => {
      if (isNil(date)) return date;
      const isInclusive = inclusives.at(index);
      if (isInclusive || isNil(isInclusive)) return date;
      if (index === 0) return addTime(date, 1);
      return addTime(date, -1);
    });
  };

export const getAscendentDates = dates => {
  const startDate = dates[0];
  const endDate = dates.at(-1);
  if (compareAsc(startDate, endDate) <= 0) return dates;
  return [undefined, undefined];
};

export const replaceUndefinedDates = boundaries => dates =>
  dates.map((date, index) => (getIsValidDate(date) ? date : boundaries.at(index)));

export function getDatesFromSdmxPeriod(frequencyType, period) {
  if (arguments.length < 2) return p => getDatesFromSdmxPeriod(frequencyType, p);
  return parseDateFromSdmxPeriod(frequencyType, period).map(date =>
    getIsValidDate(date) ? date : undefined,
  );
}

export function getSdmxPeriod(frequency, date) {
  if (arguments.length < 2) return d => getSdmxPeriod(frequency, d);
  return getIsValidDate(date) ? applyFormat(frequency)(date) : undefined;
}

export const getIntervalPeriod =
  datesBoundaries =>
  (frequency, dates = []) => {
    if (isNil(dates)) return [0, getDifferenceBetweenDates(frequency, datesBoundaries) + 1];
    if (dates.every(isNil)) return [0, getDifferenceBetweenDates(frequency, datesBoundaries) + 1];
    return [
      getDifferenceBetweenDates(frequency, replaceUndefinedDates(datesBoundaries)(dates)) + 1,
      getDifferenceBetweenDates(frequency, datesBoundaries) + 1,
    ];
  };

export const changeFrequency = frequency => obj => {
  const keys = Object.keys(obj);
  const selected = keys.includes(frequency) ? frequency : keys[0];
  return isEmpty(selected) || isNil(selected) ? defaultFrequency : selected;
};

// ------------------------------------------------------------------

export const getSubstructedDate = (date, _value, _freq) => {
  const freq = isNil(_freq) ? defaultFrequency : _freq;
  const isValid = getIsValidDate(date);
  const value = Number(_value);

  if (isNil(_value) || !isValid) return;
  let newDate = date;
  if (freq.startsWith('A')) {
    const offset = getAnnualOffset(freq);
    newDate = dfAddYears(date, (-value + 1) * offset);
  }
  if (freq === 'S') {
    newDate = dfAddMonths(date, (-value + 1) * 6);
  }
  if (freq === 'Q') {
    newDate = dfAddMonths(date, (-value + 1) * 3);
  }
  if (freq === 'M') {
    newDate = dfAddMonths(date, -value + 1);
  }
  if (freq === 'W' || freq === 'B') {
    newDate = dfAddDays(date, (-value + 1) * 7);
  }
  if (freq === 'D') {
    newDate = dfAddDays(date, -value + 1);
  }
  if (freq === 'H') {
    newDate = dfAddHours(date, -value + 1);
  }
  if (freq === 'N') {
    newDate = dfAddMinutes(date, -value + 1);
  }
  newDate = dfAddSeconds(newDate, -value + 1);
  return newDate;
};

export const getFrequencyFromDataInterval = ({ start, end }) => {
  const interval = { start: new Date(start), end: dfAddSeconds(new Date(end), 1) };
  const { years, months, days, hours, minutes } = intervalToDuration(interval);
  if (years) {
    return years === 1 ? 'A' : `A${years}`;
  }
  if (months) {
    switch (months) {
      case 6:
        return 'S';
      case 3:
        return 'Q';
      case 1:
        return 'M';
      default:
        return null;
    }
  }
  if (days) {
    switch (days) {
      case 7:
        return 'W';
      case 1:
        return 'D';
      default:
        return null;
    }
  }
  if (hours === 1) {
    return 'H';
  }
  if (minutes === 1) {
    return 'N';
  }
  return null;
};

export const getDataFrequency = ({ attributes, dimensions }) => {
  const frequencyArtefact = getFrequencyArtefact({ attributes, dimensions });
  if (!isNil(frequencyArtefact)) {
    return propOr([], 'values', frequencyArtefact)[0]?.id;
  }
  const timePeriod = rules.getTimePeriodDimension(dimensions);
  if (isNil(timePeriod)) {
    return undefined;
  }
  const { start, end } = path(['values', 0], timePeriod);
  const baseFreq = getFrequencyFromDataInterval({ start, end });
  if (baseFreq) {
    return baseFreq;
  }
  // dirty way to handle the possibility of a switch to summer or winter time during the interval ...
  const endMinusOneHour = subHours(new Date(end), 1);
  const minOneHFreq = getFrequencyFromDataInterval({ start, end: endMinusOneHour });
  const endPlusOneHour = dfAddHours(new Date(end), 1);
  const plusOneHFreq = getFrequencyFromDataInterval({ start, end: endPlusOneHour });
  return minOneHFreq || plusOneHFreq;
};
